import React from 'react';
import { CheckInResponse } from '../../../types';

export interface EnrichedCheckIn extends CheckInResponse {
  membershipTier?: string;
  notes_count?: number;
}

interface CheckInsTableProps {
  checkIns: EnrichedCheckIn[];
  isLoading: boolean;
  error: string | null;
  onViewUser?: (userId: string) => void;
}

const CheckInsTable: React.FC<CheckInsTableProps> = ({
  checkIns,
  isLoading,
  error,
  onViewUser,
}) => {
  // Format timestamp to MM/DD/YYYY
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Format quota type for display
  const formatQuotaType = (quotaType: string): string => {
    if (quotaType === 'day_pass') return 'Day Pass';
    if (quotaType === 'membership') return 'Membership';
    return quotaType;
  };

  if (isLoading && checkIns.length === 0) {
    return <div className="table-loading">Loading check-ins...</div>;
  }

  if (error && checkIns.length === 0) {
    return (
      <div className="table-error">
        <p>{error}</p>
      </div>
    );
  }

  if (checkIns.length === 0) {
    return <div className="table-empty">No check-ins found for the selected filters.</div>;
  }

  return (
    <div className="table-view">
      {isLoading && checkIns.length > 0 && (
        <div className="background-loading-indicator">
          <div className="spinner-small"></div>
          <span>Loading...</span>
        </div>
      )}

      <table className="users-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Email</th>
            <th>Member</th>
            <th>Notes</th>
            <th>Space</th>
            <th>Date</th>
            <th>Check In Type</th>
          </tr>
        </thead>
        <tbody>
          {checkIns.map((checkIn) => {
            // Get user name from enriched data or fall back to ID
            const userName = checkIn.user 
              ? [checkIn.user.first_name, checkIn.user.last_name].filter(Boolean).join(' ').trim() || checkIn.user.display_name || checkIn.user.email
              : checkIn.user_id;
            
            const userEmail = checkIn.user?.email || 'N/A';
            const spaceName = checkIn.space?.name || checkIn.space_id;
            const membershipTier = checkIn.membershipTier;

            return (
            <tr 
                key={checkIn.id}
                onClick={() => onViewUser?.(checkIn.user_id)}
                className={onViewUser ? 'table-row' : ''}
              >
                <td className="user-name-cell">
                  <div className="user-name">{userName}</div>
                </td>
                <td>{userEmail}</td>
                <td>
                  {membershipTier ? (
                    <span 
                      className="status-badge membership-yes membership-with-info" 
                      data-tooltip={`Member: ${membershipTier}`}
                    >
                      Yes <span className="info-indicator">ⓘ</span>
                    </span>
                  ) : (
                    <span className="status-badge membership-no">No</span>
                  )}
                </td>
                <td>
                  {checkIn.notes_count && checkIn.notes_count > 0 ? (
                    <span className="notes-badge" title={`${checkIn.notes_count} note${checkIn.notes_count !== 1 ? 's' : ''}`}>
                      📝 {checkIn.notes_count}
                    </span>
                  ) : (
                    <span className="notes-none">—</span>
                  )}
                </td>
                <td>{spaceName}</td>
                <td>{formatTimestamp(checkIn.timestamp)}</td>
                <td>
                  <span className={`quota-badge ${checkIn.used_quota_type}`}>
                    {formatQuotaType(checkIn.used_quota_type)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CheckInsTable;
